import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Save, X, Package } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminUrunler = () => {
  const [urunler, setUrunler] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUrun, setEditingUrun] = useState(null);
  const [formData, setFormData] = useState({
    urun_adi: "",
    veri_cek: false,
    sira_no: "",
    code: "",
    alis: "",
    satis: "",
    eklenecek_tutar: "",
    eklenecek_yer: "",
  });

  const getAuthHeader = () => {
    const auth = sessionStorage.getItem("adminAuth");
    return auth ? { Authorization: `Basic ${auth}` } : {};
  };

  const fetchUrunler = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/urunler`, {
        headers: getAuthHeader(),
      });
      setUrunler(response.data);
    } catch (error) {
      console.error("Error fetching products:", error);
      toast.error("Ürünler yüklenirken hata oluştu");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUrunler();
  }, [fetchUrunler]);

  const openAddDialog = () => {
    setEditingUrun(null);
    setFormData({
      urun_adi: "",
      veri_cek: false,
      sira_no: "",
      code: "",
      alis: "",
      satis: "",
      eklenecek_tutar: "",
      eklenecek_yer: "",
    });
    setDialogOpen(true);
  };

  const openEditDialog = (urun) => {
    setEditingUrun(urun);
    setFormData({
      urun_adi: urun.urun_adi || "",
      veri_cek: urun.veri_cek || false,
      sira_no: urun.sira_no?.toString() || "",
      code: urun.code || "",
      alis: urun.alis?.toString() || "",
      satis: urun.satis?.toString() || "",
      eklenecek_tutar: urun.eklenecek_tutar?.toString() || "",
      eklenecek_yer: urun.eklenecek_yer || "",
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const payload = {
      urun_adi: formData.urun_adi,
      veri_cek: formData.veri_cek,
      sira_no: formData.sira_no ? parseFloat(formData.sira_no) : null,
      code: formData.code || null,
      alis: parseFloat(formData.alis) || 0,
      satis: parseFloat(formData.satis) || 0,
      eklenecek_tutar: parseFloat(formData.eklenecek_tutar) || 0,
      eklenecek_yer: formData.eklenecek_yer || null,
    };

    try {
      if (editingUrun) {
        await axios.put(`${API}/admin/urunler/${editingUrun.urun_no}`, payload, {
          headers: getAuthHeader(),
        });
        toast.success("Ürün güncellendi");
      } else {
        await axios.post(`${API}/admin/urunler`, payload, {
          headers: getAuthHeader(),
        });
        toast.success("Ürün eklendi");
      }
      setDialogOpen(false);
      fetchUrunler();
    } catch (error) {
      console.error("Error saving product:", error);
      toast.error("Ürün kaydedilirken hata oluştu");
    }
  };

  const handleDelete = async (urunNo) => {
    if (!window.confirm("Bu ürünü silmek istediğinizden emin misiniz?")) return;

    try {
      await axios.delete(`${API}/admin/urunler/${urunNo}`, {
        headers: getAuthHeader(),
      });
      toast.success("Ürün silindi");
      fetchUrunler();
    } catch (error) {
      console.error("Error deleting product:", error);
      toast.error("Ürün silinirken hata oluştu");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-urunler-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 lg:mb-8">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 lg:w-12 lg:h-12 bg-amber-100 rounded-xl flex items-center justify-center">
            <Package className="w-5 h-5 lg:w-6 lg:h-6 text-amber-600" />
          </div>
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-slate-800" style={{ fontFamily: 'Playfair Display, serif' }}>
              Ürün Yönetimi
            </h1>
            <p className="text-slate-500 text-xs lg:text-sm">{urunler.length} ürün listeleniyor</p>
          </div>
        </div>
        <Button onClick={openAddDialog} className="btn-primary text-sm lg:text-base" data-testid="add-urun-button">
          <Plus className="w-4 h-4 lg:w-5 lg:h-5" />
          <span className="ml-1">Yeni Ürün</span>
        </Button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[800px]" data-testid="urunler-table">
            <thead>
              <tr className="bg-slate-50 border-b">
                <th className="text-left font-semibold text-slate-700 py-3 px-3 lg:py-4 lg:px-4 text-xs lg:text-sm">Sıra</th>
                <th className="text-left font-semibold text-slate-700 py-3 px-3 lg:py-4 lg:px-4 text-xs lg:text-sm">Ürün Adı</th>
                <th className="text-left font-semibold text-slate-700 py-3 px-3 lg:py-4 lg:px-4 text-xs lg:text-sm">Kod</th>
                <th className="text-right font-semibold text-slate-700 py-3 px-3 lg:py-4 lg:px-4 text-xs lg:text-sm">Alış</th>
                <th className="text-right font-semibold text-slate-700 py-3 px-3 lg:py-4 lg:px-4 text-xs lg:text-sm">Satış</th>
                <th className="text-right font-semibold text-slate-700 py-3 px-3 lg:py-4 lg:px-4 text-xs lg:text-sm">Ek Tutar</th>
                <th className="text-center font-semibold text-slate-700 py-3 px-3 lg:py-4 lg:px-4 text-xs lg:text-sm">Ek Yer</th>
                <th className="text-center font-semibold text-slate-700 py-3 px-3 lg:py-4 lg:px-4 text-xs lg:text-sm">Veri Çek</th>
                <th className="text-center font-semibold text-slate-700 py-3 px-3 lg:py-4 lg:px-4 text-xs lg:text-sm">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {urunler.map((urun) => (
                <tr key={urun.urun_no} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`urun-row-${urun.urun_no}`}>
                  <td className="py-3 px-3 lg:py-4 lg:px-4 mono-numbers text-xs lg:text-sm">{urun.sira_no}</td>
                  <td className="py-3 px-3 lg:py-4 lg:px-4 font-medium text-slate-800 text-xs lg:text-sm">{urun.urun_adi}</td>
                  <td className="py-3 px-3 lg:py-4 lg:px-4 text-slate-500 text-xs lg:text-sm">{urun.code || "-"}</td>
                  <td className="py-3 px-3 lg:py-4 lg:px-4 mono-numbers font-semibold text-right text-xs lg:text-sm">
                    {urun.alis?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                  </td>
                  <td className="py-3 px-3 lg:py-4 lg:px-4 mono-numbers font-semibold text-right text-xs lg:text-sm">
                    {urun.satis?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                  </td>
                  <td className="py-3 px-3 lg:py-4 lg:px-4 mono-numbers text-right text-xs lg:text-sm">
                    {urun.eklenecek_tutar?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                  </td>
                  <td className="py-3 px-3 lg:py-4 lg:px-4 text-center">
                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                      urun.eklenecek_yer === 'A' ? 'bg-blue-100 text-blue-700' : 
                      urun.eklenecek_yer === 'S' ? 'bg-green-100 text-green-700' : 
                      'bg-slate-100 text-slate-500'
                    }`}>
                      {urun.eklenecek_yer || "-"}
                    </span>
                  </td>
                  <td className="py-3 px-3 lg:py-4 lg:px-4 text-center">
                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${urun.veri_cek ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                      {urun.veri_cek ? "Aktif" : "Pasif"}
                    </span>
                  </td>
                  <td className="py-3 px-3 lg:py-4 lg:px-4">
                    <div className="flex items-center justify-center gap-1">
                      <button
                        onClick={() => openEditDialog(urun)}
                        className="p-1.5 lg:p-2 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                        data-testid={`edit-urun-${urun.urun_no}`}
                      >
                        <Pencil className="w-3.5 h-3.5 lg:w-4 lg:h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(urun.urun_no)}
                        className="p-1.5 lg:p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        data-testid={`delete-urun-${urun.urun_no}`}
                      >
                        <Trash2 className="w-3.5 h-3.5 lg:w-4 lg:h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Playfair Display, serif' }}>
              {editingUrun ? "Ürün Düzenle" : "Yeni Ürün Ekle"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-3 lg:gap-4">
              <div className="col-span-2">
                <Label htmlFor="urun_adi" className="text-sm">Ürün Adı</Label>
                <Input
                  id="urun_adi"
                  value={formData.urun_adi}
                  onChange={(e) => setFormData({ ...formData, urun_adi: e.target.value })}
                  required
                  className="text-sm"
                  data-testid="input-urun-adi"
                />
              </div>
              <div>
                <Label htmlFor="sira_no" className="text-sm">Sıra No</Label>
                <Input
                  id="sira_no"
                  type="number"
                  step="0.1"
                  value={formData.sira_no}
                  onChange={(e) => setFormData({ ...formData, sira_no: e.target.value })}
                  className="text-sm"
                  data-testid="input-sira-no"
                />
              </div>
              <div>
                <Label htmlFor="code" className="text-sm">Kod</Label>
                <Input
                  id="code"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  className="text-sm"
                  data-testid="input-code"
                />
              </div>
              <div>
                <Label htmlFor="alis" className="text-sm">Alış</Label>
                <Input
                  id="alis"
                  type="number"
                  step="0.01"
                  value={formData.alis}
                  onChange={(e) => setFormData({ ...formData, alis: e.target.value })}
                  className="text-sm"
                  data-testid="input-alis"
                />
              </div>
              <div>
                <Label htmlFor="satis" className="text-sm">Satış</Label>
                <Input
                  id="satis"
                  type="number"
                  step="0.01"
                  value={formData.satis}
                  onChange={(e) => setFormData({ ...formData, satis: e.target.value })}
                  className="text-sm"
                  data-testid="input-satis"
                />
              </div>
              <div>
                <Label htmlFor="eklenecek_tutar" className="text-sm">Eklenecek Tutar</Label>
                <Input
                  id="eklenecek_tutar"
                  type="number"
                  step="0.01"
                  value={formData.eklenecek_tutar}
                  onChange={(e) => setFormData({ ...formData, eklenecek_tutar: e.target.value })}
                  className="text-sm"
                  data-testid="input-eklenecek-tutar"
                />
              </div>
              <div>
                <Label htmlFor="eklenecek_yer" className="text-sm">Eklenecek Yer</Label>
                <Select
                  value={formData.eklenecek_yer}
                  onValueChange={(value) => setFormData({ ...formData, eklenecek_yer: value })}
                >
                  <SelectTrigger className="text-sm" data-testid="select-eklenecek-yer">
                    <SelectValue placeholder="Seçiniz" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="A">A (Alış)</SelectItem>
                    <SelectItem value="S">S (Satış)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2 flex items-center gap-3">
                <Switch
                  id="veri_cek"
                  checked={formData.veri_cek}
                  onCheckedChange={(checked) => setFormData({ ...formData, veri_cek: checked })}
                  data-testid="switch-veri-cek"
                />
                <Label htmlFor="veri_cek" className="text-sm">Veri Çek (Aktif)</Label>
              </div>
            </div>
            <div className="flex justify-end gap-2 lg:gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                className="text-sm"
                data-testid="cancel-button"
              >
                <X className="w-4 h-4 mr-1" />
                İptal
              </Button>
              <Button type="submit" className="btn-primary text-sm" data-testid="save-button">
                <Save className="w-4 h-4 mr-1" />
                Kaydet
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUrunler;
